import type { ColumnDef } from '@tanstack/react-table';
import {
  Card,
  InputDateTime,
  InputSelect,
  InputText,
  TableListData,
} from 'components';
import {
  Button,
  InputWraper,
  useForm,
  IUseFormStructureItem,
} from 'modules';
import React, { useCallback, useState } from 'react';
import {
  useGetAuditLogsAdminExportExQuery,
  useGetAdminPermissionsQuery,
} from 'services';
import type {
  AuditLogDetailEntity,
  GetListAuditLogEntity,
} from 'services/openapi';
import { DEFAULT_PAGE } from 'configs';
import ExcelJS from 'exceljs';
import saveAs from 'file-saver';
import {
  formatColumn,
  formatRowData,
} from 'configs/cell_xlsx';

import {
  columnsDefined,
  convertVariables,
  type FilterType,
  getInitFilterValues,
  exportHeaders,
} from './config';
import { format } from 'date-fns';
import { useLocation } from 'react-router-dom';
import * as Yup from 'yup';
import {
  GetAuditLogsAdminDTO,
  useGetAuditLogsAdminQuery,
} from 'services/user/queries/useGetAuditLogsAdminQuery';

const AuditLogsAdminList = () => {
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const initFilterValues = getInitFilterValues(urlParams);

  console.log('initFilterValues', initFilterValues);
  const [variables, setVariables] = useState<FilterType>(initFilterValues);
  const [dataTable, setDataTable] = useState([]);

  const { data: dataScreenActionPermissions } =
    useGetAdminPermissionsQuery();

  const dataScreenActionPermissionsLst = useCallback(() => {
    const data = dataScreenActionPermissions;

    if (!data) return [];
    const lstRess = [
      {
        label: 'Tất cả',
        value: 'Tất cả',
      },
    ];
    return [
      ...lstRess,
      ...dataScreenActionPermissions?.data?.map((item: any) => ({
        label: item,
        value: item,
      })),
    ];
  }, [dataScreenActionPermissions]);

  const handleFilter = useCallback(
    (e: FilterType) => {
      setVariables({
        ...variables,
        ...e,
        refreshData: variables.refreshData + 1,
      });
    },
    [variables],
  );

  const filterOptions: IUseFormStructureItem[] = [
    {
      name: 'actorId',
      label: 'Admin',
      defaultValue: initFilterValues.actorId,
    },
    {
      name: 'action',
      label: 'Action',
      defaultValue: initFilterValues.action,
    },
    {
      name: 'startDate',
      label: 'Ngày & giờ bắt đầu',
      defaultValue: initFilterValues.startDate,
      validate: Yup.string()
        .required('Vui lòng chọn Ngày & giờ bắt đầu')
        .test(
          'comp-fromdate-todate',
          'Ngày & giờ bắt đầu phải nhỏ hơn Ngày & giờ kết thúc',
          function (value) {
            if (value)
              return (
                new Date(value).getTime() <
                new Date(this.parent?.endDate).getTime()
              );
            return false;
          },
        )
        .test(
          'comp-fromdate-todate-60-days',
          'Khoảng cách Ngày & giờ bắt đầu và Ngày & giờ kết thúc không vượt quá 60 ngày',
          function (value) {
            if (value) {
              const dateEnd: Date = new Date(this.parent?.endDate);
              dateEnd.setHours(0, 0, 0, 0);
              dateEnd.setDate(dateEnd.getDate() - 60);
              const dateStart: Date = new Date(value);
              dateEnd.setHours(0, 0, 0, 0);
              return dateEnd.getTime() <= dateStart.getTime();
            }
            return false;
          },
        ),
    },
    {
      name: 'endDate',
      label: 'Ngày & giờ kết thúc',
      defaultValue: initFilterValues.endDate,
      validate: Yup.string()
        .required('Vui lòng chọn Ngày & giờ kết thúc')
        .test(
          'comp-fromdate-todate',
          'Ngày & giờ kết thúc phải lớn hơn Ngày & giờ bắt đầu',
          function (value) {
            if (value)
              return (
                new Date(this.parent?.startDate).getTime() <
                new Date(value).getTime()
              );
            return false;
          },
        )
        .test(
          'comp-fromdate-todate-60-days',
          'Khoảng cách Ngày & giờ bắt đầu và Ngày & giờ kết thúc không vượt quá 60 ngày',
          function (value) {
            if (value) {
              const dateEnd: Date = new Date(value);
              dateEnd.setHours(0, 0, 0, 0);
              dateEnd.setDate(dateEnd.getDate() - 60);
              const dateStart: Date = new Date(this.parent?.startDate);
              dateEnd.setHours(0, 0, 0, 0);
              return dateEnd.getTime() <= dateStart.getTime();
            }
            return false;
          },
        ),
    },
  ];

  const { getInputProps, handleSubmit } = useForm({
    structure: filterOptions,
    onSubmit: handleFilter,
  });

  const { isFetching: isLoadingExport, refetch } =
    useGetAuditLogsAdminExportExQuery({
      ...convertVariables(variables),
      page: '1',
      pageSize: String((dataTable as unknown as GetListAuditLogEntity)?.total),
    });

  const handleExportExcel = async () => {
    const allData = await refetch({
      ...convertVariables(variables),
      page: DEFAULT_PAGE,
      pageSize: String((dataTable as unknown as GetListAuditLogEntity)?.total),
    } as any);

    const dataArray = allData?.data?.data;

    const result = Array.isArray(dataArray) ? dataArray : [];

    //
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Sheet1');

    // Columns
    const headerColumn = exportHeaders.map((header) => {
      return {
        header: header,
        key: header,
        width: 40,
      };
    });
    worksheet.columns = headerColumn;

    const firstRow = worksheet.getRow(1);
    firstRow.eachCell((cell) => formatColumn(cell));

    // Data
    result.map((item: AuditLogDetailEntity, index: number) => {
      const row = worksheet.addRow([
        index + 1,
        item.actorPhoneNumber,
        item.action,
        item.metadata,
        item.ipAddress,
        item.userAgent,
        format(new Date(String(item.createdAt)), 'HH:mm:ss dd/MM/yyyy'),
      ]);
      row.eachCell((cell) => formatRowData(cell));
    });

    // Generate Excel and trigger file download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const now = new Date();
    saveAs(
      blob,
      `Danh_sach_Logs_Admin_${now.toLocaleTimeString('vn-VN', { hour12: false }).replace(/:/g, '_')}_${now.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '_')}.xlsx`,
    );
  };

  return (
    <>
      <Card className='container-fluid mb20'>
        <form onSubmit={handleSubmit}>
          <div className='row'>
            <div className='col-lg-3 col-12'>
              <InputWraper
                inputProps={getInputProps('actorId')}
                renderInput={InputText}
              />
            </div>

            <div className='col-lg-3 col-12'>
              <InputWraper
                inputProps={getInputProps('action')}
                renderInput={(props) => (
                  <InputSelect
                    {...props}
                    options={dataScreenActionPermissionsLst()}
                  />
                )}
              />
            </div>

            <div className='col-lg-3 col-12'>
              <InputWraper
                inputProps={getInputProps('startDate')}
                isRequired
                renderInput={InputDateTime}
              />
            </div>

            <div className='col-lg-3 col-12'>
              <InputWraper
                inputProps={getInputProps('endDate')}
                isRequired
                renderInput={InputDateTime}
              />
            </div>
          </div>
          <div className='textCenter mt16 wrapButtonFilter'>
            <Button label={'Tìm kiếm'} type='submit' buttonType='success' />
            <Button
              className='ml15'
              onClick={handleExportExcel}
              label={'Tải về DS'}
              type='submit'
              buttonType='info'
              isLoading={isLoadingExport}
            />
          </div>
        </form>
      </Card>

      <TableListData<GetAuditLogsAdminDTO>
        columns={columnsDefined as ColumnDef<GetAuditLogsAdminDTO>[]}
        useQuery={useGetAuditLogsAdminQuery}
        variables={variables}
        setVariables={setVariables}
        convertVariables={convertVariables}
        filterKey='searchDetailContent'
        orderColumn
        callBackData={setDataTable}
      />
    </>
  );
};

export default AuditLogsAdminList;
